import { PRIVACY_CLASSNAMES } from "@/lib/analytics/shared";
import { getTextFromMessage } from "@/lib/messages";
import { classNames, cn, generateId } from "@/lib/utils";
import { ChatMessageContent, ChatMessageData, ChatMessageRole, ToolCall } from "@/services/promptEditor.service";
import { ToolResult } from "@/services/v4.service";
import { InlineMenu } from "@components/SimpleTable/InlineMenu";
import { ContextMenuItem } from "@components/library/ContextMenu";
import IconButton from "@components/library/IconButton";
import { ChevronsDownUpIcon, ChevronsUpDownIcon, ToolIcon } from "@components/library/Icons";
import Tooltip, { ConditionalTooltip } from "@components/library/Tooltip";
import { MinusCircleIcon, PhotographIcon } from "@heroicons/react/outline";
import { useEffect, useMemo, useRef, useState } from "react";
import TruncateMarkup from "react-truncate-markup";
import { ContentRow } from "../ContentRow";
import { formattedPromptHtml } from "../FormattedPrompt";
import { ShowLogDrawerButton, Datum, ShowLogDrawerIconButton } from "../PlaygroundData";
import { ChatImages } from "./ChatImages";
import MessageEditor from "./MessageEditor";
import MessageName from "./MessageName";
import MessageRoleDropdown from "./MessageRoleDropdown";
import TypingIndicator from "./TypingIndicator";
import { UploadImageDropzoneWrapper } from "./UploadImageDropzoneWrapper";
import { TemplateLanguage } from "@/types/app/prompt";
import ThinkingRenderer from "./ThinkingRenderer";
import { Tool } from "@/types/app/tool";
import { FileLabelWithPopover } from "@components/library/Entities/FileLabelWithPopover";
import { ToolTag } from "../VariableTags";
import JSONEditor from "@components/llama/Code/JSONEditor";
import { usePromptEditorContext } from "@/context/PromptEditorContext";
import usePrevious from "@/lib/use-previous";
import { AutoFormattedText } from "@components/library/FormattedText";
import * as Collapsible from "@radix-ui/react-collapsible";
import { CaretDown, CaretUp, Warning } from "@phosphor-icons/react";
import { EntityLabel } from "@components/library/Entities/EntityLabel";

export const Message = ({
  message,
  setMessage,
  onRemove,
  onRun,
  handleSave,
  inTemplate,
  hasToolCalling,
  toolMessages,
  updateToolMessage,
  overrideToolMessageEditable,
}: {
  message: ChatMessageData;
  setMessage?: (message: ChatMessageData) => void;
  onRemove?: () => void;
  onRun?: () => void;
  handleSave: () => void;
  inTemplate?: boolean;
  hasToolCalling?: boolean;
  toolMessages?: ChatMessageData[];
  updateToolMessage?: (toolCallId: string, output: string) => void;
  overrideToolMessageEditable?: boolean;
}) => {
  // Generate an ID for the render if one doesn't exist.
  // This is a bit of an unfortunate hack to deal with the fact that we
  // have ChatMessage and ChatMessageData types which have IDs or not.
  const [messageId, _] = useState<string>(message._id || generateId("message"));
  const { datums } = usePromptEditorContext();
  const ref = useRef<HTMLDivElement | null>(null);
  const [isNewMessage, setIsNewMessage] = useState(true);
  const prevContent = usePrevious(message.content);

  useEffect(() => {
    // Smooth how the new message is added to the chat.
    // Primary use case for "Add to Messages" button for Agent file
    if (isNewMessage) {
      const timer = setTimeout(() => setIsNewMessage(false), 150);
      return () => clearTimeout(timer);
    }
  }, [isNewMessage]);

  useEffect(() => {
    if (ref.current && message._status === "loading" && prevContent !== message.content) {
      // Find the chat container - likely need to traverse up a few levels
      // We'll look for a scrollable container
      let container = ref.current.parentElement;
      while (container) {
        const style = window.getComputedStyle(container);
        if (style.overflowY === "auto" || style.overflowY === "scroll") {
          break;
        }
        container = container.parentElement;
      }

      if (container) {
        // Get the height difference of the new content
        const currentHeight = (ref.current as HTMLElement).offsetHeight;
        const prevSibling = ref.current.previousElementSibling as HTMLElement;
        const prevHeight = prevSibling?.offsetHeight || 0;
        const heightDiff = currentHeight - prevHeight;

        if (heightDiff > 0) {
          // Scroll by the difference in height
          container.scrollBy({
            top: heightDiff,
            behavior: "smooth",
          });
        }
      }
    }
  }, [message.content, message._status, prevContent]);

  const setRole = setMessage ? (role: ChatMessageRole) => setMessage({ ...message, role }) : undefined;
  const setContent = useMemo(
    () => (setMessage ? (content: ChatMessageData["content"]) => setMessage({ ...message, content }) : undefined),
    [setMessage, message],
  );
  const editable = setMessage !== undefined;
  const setName = setMessage ? (name: string) => setMessage({ ...message, name }) : undefined;
  const addToolCall = setMessage
    ? () =>
        setMessage({
          ...message,
          tool_calls: [
            ...(message.tool_calls || []),
            { id: generateId(), function: { name: "", arguments: "{}" }, type: "function" },
          ],
        })
    : undefined;

  const controls = (
    <div
      className={classNames(
        `flex items-center gap-0 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 group-has-[.portal-active]:opacity-100`,
      )}
    >
      {onRemove ? (
        // Neg margin to not cause the slightest layout shift when the controls appear.
        <div className="-my-2">
          <IconButton
            tooltip="Remove message"
            Icon={MinusCircleIcon}
            size={24}
            shade="black"
            onClick={(event) => {
              event.stopPropagation();
              onRemove();
            }}
          />
        </div>
      ) : null}
      {inTemplate ? null : <ShowLogDrawerIconButton logId={message.logId} />}

      <InlineMenu className="" size={24} shade="black">
        <ConditionalTooltip
          condition={!(hasToolCalling && message.role === "assistant")}
          content="Only available assistant messages"
        >
          <ContextMenuItem
            withinDropdownMenu
            IconLeft={ToolIcon}
            disabled={!(hasToolCalling && message.role === "assistant")}
            onClick={() => {
              if (!addToolCall) return;
              addToolCall();
            }}
          >
            Insert tool call
          </ContextMenuItem>
        </ConditionalTooltip>
        <ContextMenuItem
          withinDropdownMenu
          IconLeft={PhotographIcon}
          onClick={(event) => {
            // stopPropagation needed or the setContent call here is ignored for some reason
            // (I'm guessing something else is calling setContent immediately after this onClick otherwise)
            event.stopPropagation();
            addImageContent({ type: "image_url", image_url: { url: "", detail: "auto" } });
          }}
        >
          Insert image
        </ContextMenuItem>
      </InlineMenu>
    </div>
  );

  // Editor only changes text content.
  const editorOnContentChange = (content: string) => {
    if (setContent) {
      if (Array.isArray(message.content)) {
        // If the message content is an array
        const textContentItem = message.content.find((contentItem) => contentItem.type === "text");
        if (!textContentItem) {
          // If there is no text content item, create one
          setContent([...message.content, { type: "text", text: content }]);
        } else {
          // If there is a text content item, update it
          setContent(
            message.content.map((contentItem) =>
              contentItem.type === "text" ? { ...contentItem, text: content } : contentItem,
            ),
          );
        }
      } else {
        // If the message content is not an array (and is just a string)
        setContent(content);
      }
    }
  };

  /** Insert a new image content item into the message content. */
  const addImageContent = (newImageContent: ChatMessageContent | ChatMessageContent[]) => {
    if (setContent) {
      setContent(
        message.content && Array.isArray(message.content)
          ? [...message.content, ...(Array.isArray(newImageContent) ? newImageContent : [newImageContent])]
          : [
              { type: "text", text: message.content || "" },
              ...(Array.isArray(newImageContent) ? newImageContent : [newImageContent]),
            ],
      );
    }
  };

  const addDataUrls = (dataUrls: string[]) => {
    const imageContentItems: ChatMessageContent[] = dataUrls.map((url) => ({
      type: "image_url",
      image_url: { url, detail: "auto" },
    }));
    addImageContent(imageContentItems);
  };

  // Show thinking if the message is an assistant message, has thinking content, and the
  // content is either not falsy or the message has stopped loading. The content falsy
  // check allows us to render the thinking while normal tokens are still streaming. The second
  // check is a fallback to ensure we render tool calls.
  // TODO: We are not streaming the thinking content as it's not part of the response because it
  // interacts badly with the absolute positioning of the <TypingIndicator />.
  const showThinking =
    message.role === "assistant" && message.thinking && (message.content !== "" || message._status === "not_loading");

  return (
    <UploadImageDropzoneWrapper onDrop={addDataUrls} multiple>
      <div
        className={cn("w-full py-10", PRIVACY_CLASSNAMES["moderate"], isNewMessage && "animate-fade-in")}
        data-messageid={messageId}
      >
        <ContentRow
          role={
            <MessageRoleDropdown
              role={message.role}
              setRole={editable ? setRole : undefined}
              toolRoleEnabled={hasToolCalling}
            />
          }
          controls={controls}
        >
          {/* <pre className="text-10-10">{JSON.stringify(message, null, 2)}</pre> */}
          <div className="flex items-center justify-between ">
            {message.role === "tool" && (
              <>
                <MessageName
                  maxLength={64}
                  classNameOverrides=""
                  value={message.name || ""}
                  setValue={editable ? setName : undefined}
                />
                <ToolIdInput
                  value={message.tool_call_id || ""}
                  setValue={
                    editable ? (tool_call_id) => setMessage && setMessage({ ...message, tool_call_id }) : undefined
                  }
                  className=""
                />
              </>
            )}
          </div>
          {/* @ts-ignore message.thinking is not falsy, covered by showThinking */}
          {showThinking && <ThinkingRenderer thinking={message.thinking} className="mt-10" />}
          {editable || getTextFromMessage(message.content) !== "" ? (
            <>
              <MessageEditor
                classNameOverrides={message.role === "tool" ? "text-12-16 font-mono" : ""}
                initialContent={getTextFromMessage(message.content)}
                onContentChange={editable ? editorOnContentChange : undefined}
                loading={message._status === "loading"}
                placeholder={
                  message.role === "assistant" && message.tool_calls ? "" : ROLE_TO_PLACEHOLDER[message.role]
                }
                onRun={onRun}
                handleSave={handleSave}
                hasDynamicVariables={inTemplate}
                ref={ref}
              />
            </>
          ) : (
            <div className={"mt-10 w-full text-14-24"} ref={ref}>
              <AutoFormattedText text={getTextFromMessage(message.content)} />
            </div>
          )}

          {message.content === "" && message._status === "loading" && (
            <div className="absolute left-20 top-20 animate-fade-in bg-white">
              <TypingIndicator />
            </div>
          )}

          {message.role === "assistant" && (
            <AssistantToolCalls
              datums={datums}
              message={message}
              toolMessages={toolMessages}
              setMessage={setMessage}
              updateToolMessage={updateToolMessage}
              overrideToolMessageEditable={overrideToolMessageEditable}
            />
          )}

          <ChatImages message={message} setContent={setContent} />
        </ContentRow>
      </div>
    </UploadImageDropzoneWrapper>
  );
};

/**
 * Renders the tool calls for an assistant message.
 *
 * Also renders the corresponding tool messages.
 *
 * - message: the assistant message with tool calls
 * - toolMessages: the tool messages corresponding to the tool calls
 * - setMessage: the function to set the assistant messages (and thereby update tool calls)
 * - updateToolMessage: the function to update a tool call message with results
 */
export const AssistantToolCalls = ({
  message,
  toolMessages,
  setMessage,
  updateToolMessage,
  datums,
  overrideToolMessageEditable,
}: {
  message: ChatMessageData;
  toolMessages?: ChatMessageData[];
  setMessage?: (message: ChatMessageData) => void;
  updateToolMessage?: (toolCallId: string, output: string) => void;
  datums?: Datum[];
  overrideToolMessageEditable?: boolean;
}) => {
  return (
    message.tool_calls?.map((toolCall, i) => {
      const toolMessage = toolMessages?.find((m) => m.tool_call_id === toolCall.id);
      return (
        // Using index as key as ID is not stable (can be edited)
        <div key={`tool-${i}`} className="mb-4 mt-8">
          <ToolCallEditor
            datums={datums}
            toolCall={toolCall}
            setToolCall={
              setMessage
                ? (toolCall) => {
                    setMessage({
                      ...message,
                      tool_calls: message.tool_calls?.map((tc, j) => (i === j ? toolCall : tc)),
                    });
                  }
                : undefined
            }
            removeToolCall={
              setMessage
                ? () => {
                    const remainingToolCalls = message.tool_calls?.filter((_, j) => i !== j);
                    setMessage({
                      ...message,
                      tool_calls: remainingToolCalls?.length ? remainingToolCalls : undefined,
                    });
                  }
                : undefined
            }
            toolMessage={toolMessage}
            updateToolMessage={updateToolMessage}
            overrideToolMessageEditable={overrideToolMessageEditable}
          />
        </div>
      );
    }) ?? null
  );
};

export const ToolCallEditor = ({
  toolCall,
  setToolCall,
  removeToolCall,
  tool,
  toolMessage,
  updateToolMessage,
  /*
  datums is used by Editor only. Not used by ReviewView.
  */
  datums,
  overrideToolMessageEditable,
}: {
  toolCall: ToolCall;
  setToolCall?: (toolCall: ToolCall) => void;
  removeToolCall?: () => void;
  tool?: Tool;
  toolMessage?: ChatMessageData;
  updateToolMessage?: (toolCallId: string, output: string) => void;
  datums?: Datum[];
  /**
   * Ensures we show the tool message (i.e. tool response), even if empty, and allow it to be edited.
   * E.g. after a user has clicked "Add to Messages" on a tool call in Prompt Editor.
   */
  overrideToolMessageEditable?: boolean;
}) => {
  let defaultValue = toolCall.function.arguments;
  try {
    defaultValue = JSON.stringify(JSON.parse(toolCall.function.arguments), null, 2);
  } catch (e) {
    // Do nothing
  }

  const anyChatLoading = datums?.some((d) => d.type === "chat" && d.status === "loading");
  const prevAnyChatLoading = usePrevious(anyChatLoading); // We need to trigger a refresh when we've just changed from loading to not loading.
  const triggerRefresh = anyChatLoading || prevAnyChatLoading;
  const anyChatSuspended = datums?.some((d) => d.type === "chat" && d.status === "suspended");

  const toolMessageContent = toolMessage
    ? typeof toolMessage.content === "string"
      ? toolMessage.content
      : JSON.stringify(toolMessage.content, null, 2)
    : "";

  const toolMessageLoading = toolMessage?._status === "loading";

  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (anyChatSuspended && toolMessageContent === "") {
      setExpanded(true);
    }
  }, [toolMessageContent, anyChatSuspended]);

  const contextMenuItems = useMemo(() => {
    const items = [];
    if (removeToolCall) {
      items.push(
        <ContextMenuItem
          withinDropdownMenu
          IconLeft={MinusCircleIcon}
          onClick={() => removeToolCall?.()}
          disabled={true} // TODO: Update this and add replace
          className="cursor-not-allowed"
        >
          Delete tool call
        </ContextMenuItem>,
      );
    }
    return items;
  }, [removeToolCall]);

  return (
    <>
      {/* Tool call */}
      <div
        className={classNames(
          "flex flex-col overflow-hidden rounded-lg",
          expanded
            ? "shadow-card-sm"
            : "outline outline-1 outline-stroke-base-3 hover:shadow-card-sm hover:outline-none",
        )}
      >
        {/* Header */}
        <Collapsible.Root open={expanded} onOpenChange={setExpanded}>
          <Collapsible.Trigger asChild>
            <div
              className={classNames(
                "flex cursor-pointer items-center",
                expanded && "border-b border-stroke-base-25 bg-background-base-2",
              )}
            >
              <div className="flex p-8">
                {tool ? (
                  <FileLabelWithPopover file={tool} size={20} />
                ) : (
                  <ToolTag className="font-mono">{toolCall.function.name}</ToolTag>
                )}
              </div>

              {/* Tool actions */}
              <div className="relative flex h-full grow items-center justify-end gap-4 pr-8">
                <div className="absolute -left-16 h-full w-16 bg-gradient-to-r from-transparent to-background-base-1 " />
                {toolMessageLoading && expanded === false && (
                  <div className="mr-16 animate-fade-in items-center bg-white">
                    <TypingIndicator />
                  </div>
                )}
                {contextMenuItems.length > 0 && (
                  <InlineMenu size={20} shade="black" dotsOrientation="horizontal">
                    {contextMenuItems}
                  </InlineMenu>
                )}
                {expanded ? (
                  <CaretUp className="h-12 w-12 text-icon-base-2" />
                ) : (
                  <CaretDown className="h-12 w-12 text-icon-base-2" />
                )}
              </div>
            </div>
          </Collapsible.Trigger>

          <Collapsible.Content className="animate-collapsible-open data-[state=closed]:animate-collapsible-close">
            <div className="flex flex-col gap-12 px-8 py-12">
              {/** Input */}
              <div className="flex flex-col gap-6">
                <div className="text-12-16 font-medium text-text-base-2">Input</div>

                <div className="h-full w-full text-12-14">
                  <JSONEditor
                    defaultValue={defaultValue}
                    triggerRefresh={triggerRefresh ? toolMessage?.content : undefined}
                    showCopyButton={false}
                    onChange={
                      setToolCall && !triggerRefresh
                        ? (value) => {
                            if (value) {
                              setToolCall({ ...toolCall, function: { ...toolCall.function, arguments: value } });
                            }
                          }
                        : undefined
                    }
                    options={{
                      lineNumbers: true,
                      noIndentationGuides: true,
                      autosize: true,
                      scrollbar: {
                        alwaysConsumeMouseWheel: false,
                      },
                    }}
                    editorProps={{
                      theme: "llama-white",
                    }}
                  />
                </div>
              </div>
              {/** Output */}
              {toolMessage &&
                (toolMessageLoading || anyChatSuspended || toolMessageContent || overrideToolMessageEditable) && (
                  <div className="group/output flex flex-col gap-6">
                    <div className="items-center">
                      <div className="text-12-16 font-medium text-text-base-2">Output</div>
                    </div>

                    {toolMessageLoading ? (
                      <div className="mx-16 mt-6 flex h-12 animate-fade-in items-center bg-white">
                        {/* Note that the middle dot of the typing indicator is what's aligned here (for some reason).
                          and note that it's not the middle of the middle dot, but the top left of the middle dot. */}
                        <TypingIndicator />
                      </div>
                    ) : toolMessage._status === "error" ? (
                      <div className="flex h-full w-full items-center gap-8 text-12-16 text-text-danger-2">
                        <Warning className="h-16 w-16 fill-icon-danger-2" />
                        <div>{toolMessageContent}</div>
                      </div>
                    ) : (
                      <div className="h-full w-full text-12-14" data-tool-call-id={toolMessage.tool_call_id}>
                        <JSONEditor
                          defaultValue={toolMessageContent}
                          triggerRefresh={triggerRefresh ? toolMessage.content : undefined}
                          showCopyButton={false}
                          onChange={
                            updateToolMessage &&
                            !triggerRefresh &&
                            // If it's in the Output section only if the Agent has been suspended.
                            // Otherwise, if we're in the Messages section (and overrideToolMessageEditable is on, we want this editable too)
                            (anyChatSuspended || overrideToolMessageEditable)
                              ? (value) => {
                                  if (value && toolMessage.tool_call_id) {
                                    updateToolMessage(toolMessage.tool_call_id, value);
                                  }
                                }
                              : undefined
                          }
                          onMount={
                            anyChatSuspended
                              ? (editor) => {
                                  setTimeout(() => editor.focus());
                                }
                              : undefined
                          }
                          options={{
                            lineNumbers: true,
                            focusOnMount: true,
                            noIndentationGuides: true,
                            autosize: true,
                            scrollbar: {
                              alwaysConsumeMouseWheel: false,
                            },
                          }}
                          editorProps={{
                            theme: "llama-white",
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}

              {/** Open log button */}
              {toolMessage && toolMessage.logId && toolMessageLoading === false && anyChatSuspended === false && (
                <div className="pointer-events-auto flex items-center justify-between gap-4">
                  <ShowLogDrawerButton logId={toolMessage.logId} size={20} className="shadow-card-sm">
                    Open
                  </ShowLogDrawerButton>
                  {toolMessage.logId && <EntityLabel styling="ghost" entity="log" id={toolMessage.logId} size={24} />}
                </div>
              )}
            </div>
          </Collapsible.Content>
        </Collapsible.Root>
      </div>
    </>
  );
};

const ToolIdInput = ({
  value,
  setValue,
  className,
}: {
  value: string;
  setValue?: (value: string) => void;
  className?: string;
}) => {
  return (
    <Tooltip content="Tool call ID">
      <input
        placeholder="tool call ID"
        className={classNames(
          "w-[105px] px-4 py-[7px] font-mono text-12-14 text-text-base-2 placeholder:text-text-base-4 focus:outline-none focus:ring-0",
          className,
        )}
        value={value}
        onChange={(e) => setValue?.(e.target.value)}
      />
    </Tooltip>
  );
};

export const Attachment = ({
  onClose,
  className,
  children,
}: {
  onClose?: () => void;
  className?: string;
  children: React.ReactNode;
}) => {
  return (
    <div className={classNames("overflow-clip rounded-ms bg-background-base-1 shadow-card-sm", className)}>
      <div className="flex">
        <div className="w-full px-12 py-8">{children}</div>
        {onClose && (
          <div className="flex w-44 items-center justify-center border-l border-stroke-base-3 bg-background-base-2 px-8">
            <IconButton Icon={MinusCircleIcon} iconSize={24} size={40} shade={"gray"} onClick={onClose} />
          </div>
        )}
      </div>
    </div>
  );
};

const ROLE_TO_PLACEHOLDER: { [role in ChatMessageRole]: string } = {
  user: "Type your message here",
  assistant: "Insert an assistant message",
  system: "Insert a system message",
  tool: "Insert tool response",
  error: "Error", // Not sure about this being here.
};

export const TemplateMessage = ({
  message,
  inputs,
  tools,
  toolRoleEnabled,
  templateLanguage,
}: {
  message: ChatMessageData;
  inputs: Record<string, string>;
  tools?: ToolResult[];
  toolRoleEnabled?: boolean;
  templateLanguage?: TemplateLanguage | null;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [hasBeenTruncated, setHasBeenTruncated] = useState(true);
  const [showToolField, setShowToolField] = useState<boolean>(
    message.tool_calls !== null && message.tool_calls !== undefined,
  );
  const content = formattedPromptHtml({
    prompt: getTextFromMessage(message.content),
    inputs,
    tool_results: tools,
    templateLanguage: templateLanguage ?? "default",
  });

  useEffect(() => {
    setShowToolField(message.tool_calls !== null && message.tool_calls !== undefined);
  }, [message?.tool_calls]);

  const controls = hasBeenTruncated ? (
    <div className="pr-4 opacity-0 transition-opacity group-hover:opacity-100">
      <IconButton
        Icon={expanded ? ChevronsDownUpIcon : ChevronsUpDownIcon}
        onClick={() => setExpanded(!expanded)}
        size={24}
        iconSize={20}
        styling="outline"
      />
    </div>
  ) : null;

  return (
    <div className="py-10">
      <ContentRow
        role={
          <div className="opacity-70">
            <MessageRoleDropdown role={message.role} toolRoleEnabled={toolRoleEnabled} />
          </div>
        }
        controls={controls}
      >
        <div
          title="This is the chat template populated with inputs and tool results"
          className={cn(hasBeenTruncated && "cursor-pointer")}
          onClick={() => setExpanded(!expanded)}
        >
          <div
            className={classNames(
              "relative min-h-[38px] grow px-8 py-[6px] text-14-24 text-oldgray-800 opacity-70",
              PRIVACY_CLASSNAMES["moderate"],
            )}
          >
            {((message.role === "assistant" && showToolField) || message.role === "tool") && (
              <MessageName disabled classNameOverrides="!pl-0 mt-0" value={message.name || ""} setValue={() => {}} />
            )}
            {expanded ? (
              <div>{content}</div>
            ) : (
              <TruncateMarkup
                lines={2}
                ellipsis={
                  <span onClick={() => setExpanded(true)} className="text-blue cursor-pointer">
                    {" "}
                    ... more
                  </span>
                }
                onTruncate={(isTruncated) => setHasBeenTruncated(isTruncated)}
              >
                <span>{content}</span>
              </TruncateMarkup>
            )}
          </div>
        </div>
      </ContentRow>
    </div>
  );
};
